import { DependencyGraph } from "./dependency";
import { Meta, SetterConfig } from "../types";
import { FormInstance } from "antd";
import jsonLogic from "json-logic-js";
import { DependencyNode } from "./dependency";

interface EffectContext {
  meta: Meta;
  form: FormInstance;
  processingRef: React.MutableRefObject<boolean>;
  batchUpdateRef: React.MutableRefObject<Record<string, any>>;
}

export const createEffectHandler = (context: EffectContext) => {
  const { meta, form, processingRef, batchUpdateRef } = context;
  // 初始化依赖图
  const depGraph = new DependencyGraph(meta);

  // 检测并输出警告
  const cycles = depGraph.detectCycles();
  if (cycles.length > 0 && meta.dependencyAnalysis?.warnCycles) {
    console.warn("检测到循环依赖:", cycles);
  }

  // 判断是否应该更新
  const shouldUpdate = (
    node: DependencyNode,
    changedValues: Record<string, any>
  ) => {
    if (!node.updateStrategy) return true;

    const { when, keepFields } = node.updateStrategy;
    const triggered = when?.some((field) => field in changedValues);
    if (!triggered) return false;

    const conflicting = keepFields?.some((field) => field in changedValues);
    return !conflicting;
  };

  // 执行更新
  const executeUpdate = (
    node: DependencyNode,
    allValues: Record<string, any>,
    updatedValues: Record<string, any>
  ) => {
    const setter = meta.setters.props.find((s) => s.name === node.field);
    if (!setter?.effects) return;

    const { params, handler } = setter.effects;

    // 处理参数
    const processedParams = Object.entries(params).reduce(
      (acc, [key, value]) => {
        acc[key] = jsonLogic.apply(value, updatedValues);
        return acc;
      },
      {} as Record<string, any>
    );

    // 执行转换
    if (meta.transforms?.[handler]) {
      const newValue = meta.transforms[handler].getValues(processedParams);
      updatedValues[node.field] = newValue;
      batchUpdateRef.current[node.field] = newValue;
    }
  };

  return (
    changedValues: Record<string, any>,
    allValues: Record<string, any>
  ) => {
    if (processingRef.current) return;
    processingRef.current = true;

    try {
      const updatedValues = { ...allValues };
      const affectedFields = new Set<string>();

      // 收集受影响的字段
      Object.keys(changedValues).forEach((field) => {
        const node = depGraph.getNode(field);
        if (node) {
          affectedFields.add(field);
          node.dependents.forEach((dep) => affectedFields.add(dep));
        }
      });

      // 按拓扑排序处理更新
      const updateOrder = depGraph.toposort(affectedFields);
      updateOrder.forEach((field) => {
        const node = depGraph.getNode(field);
        if (node && shouldUpdate(node, changedValues)) {
          executeUpdate(node, allValues, updatedValues);
        }
      });

      // 批量更新表单
      if (Object.keys(batchUpdateRef.current).length > 0) {
        form.setFieldsValue(batchUpdateRef.current);
        batchUpdateRef.current = {};
      }
    } finally {
      processingRef.current = false;
    }
  };
};
